/* eslint-disable no-unused-expressions,compat/compat */
import React, {
  useRef,
  useMemo,
  useEffect,
  useReducer,
  createContext,
} from 'react';
import PropTypes from 'prop-types';

import { OTCore } from './core';
import { useInjectSaga } from '../injectSaga';
import { PUB_KEY } from './helpers/constants';
import { initialState, reducer } from './reducer';
import { getMediaDevices } from './helpers/utils';
import { opentokActions } from './reducer/actions';
import { RESTART_ON_REMOUNT } from '../sagaInjectors';
import { useVirtualVisit } from './hooks/useVirtualVisit';
import refreshSessionIntervalSaga from '../auth/refresh-session-interval-saga';
import {
  sentryCaptureMessage,
  trackAppAction,
} from '../../utils/sentry/sentryMethods';
import { OTSignal } from './core/signal';

function initOpenTokState(initialPendingInvitations) {
  const invitations = Array.isArray(initialPendingInvitations)
    ? initialPendingInvitations
    : [];
  return { ...initialState, invitations };
}

export const OTContext = createContext({
  state: initialState,
  startVisit: () => {},
  endVisit: () => {},
  openInviteModal: () => {},
  PUBLISHER_KEY: PUB_KEY,
  focusedId: null,
  getUserMedia: () => Promise.resolve(),
  initialMediaDevices: null,
  otPublisher: null,
  setPin: () => {},
  isProvider: true,
});

export function OTContextProvider({
  children,
  config,
  isProvider,
  initialPendingInvitations = [],
}) {
  useInjectSaga({
    key: 'refreshSessionIntervalSaga',
    saga: refreshSessionIntervalSaga,
    mode: RESTART_ON_REMOUNT,
  });

  const {
    callState,
    publisherUserData,
    handleGlobalError,
    openConfirmationPopup,
    updateAppointment,
  } = useVirtualVisit();
  const [state, dispatch] = useReducer(
    reducer,
    initialPendingInvitations,
    initOpenTokState,
  );
  const { pinnedId, activeId, lastActiveSpeaker } = state;
  const OTCORE_REF = useRef(null);

  const focusedId = useMemo(() => pinnedId || activeId, [activeId, pinnedId]);

  useEffect(() => {
    const OT_CORE = new OTCore({
      config,
      dispatch,
      isOnline: true,
      handleGlobalError,
      user: publisherUserData,
    });

    OTSignal.handleProviderJoin = () => updateAppointment();
    OTSignal.handleProviderFinishCall = status => {
      endVisit();
      openConfirmationPopup(status);
      updateAppointment();
    };
    OTCORE_REF.current = OT_CORE;

    startVisit();

    return () => {
      if (OTCORE_REF.current) {
        OTCORE_REF.current.destroy();
        callState.destroyVisit();
      }
    };
  }, []);

  useEffect(() => {
    if (lastActiveSpeaker !== null) {
      dispatch(opentokActions.setActiveId(lastActiveSpeaker));
    }
  }, [lastActiveSpeaker]);

  function endVisit() {
    trackAppAction('[ot-context] end visit');
    if (OTCORE_REF.current) {
      sentryCaptureMessage('VISIT FINISHED');
      OTCORE_REF.current.destroy();
      OTCORE_REF.current = undefined;
      callState.destroyVisit();
    }
  }

  function sendSignal({ data, type }) {
    if (OTCORE_REF.current) {
      OTCORE_REF.current.otSession.session.signal(
        {
          data,
          type,
        },
        function(error) {
          if (error) {
            console.log(`signal error (${error.name}): ${error.message}`);
          } else {
            console.log(`signal ${type} sent.`);
          }
        },
      );
    }
  }

  async function startVisit() {
    if (!config) throw new Error('No Config found');

    await OTCORE_REF.current.startVideoCall();
  }

  const setPin = ({ id }) => dispatch(opentokActions.setPin(id));

  const value = {
    state,
    sendSignal,
    startVisit,
    endVisit,
    focusedId,
    setPin,
    isProvider,
    PUBLISHER_KEY: PUB_KEY,
    getUserMedia: getMediaDevices,
    otPublisher: OTCORE_REF.current ? OTCORE_REF.current.otPublisher : null,
    initialMediaDevices: OTCORE_REF.current
      ? OTCORE_REF.current.currentMediaDevices
      : null,
  };

  return <OTContext.Provider value={value}>{children}</OTContext.Provider>;
}

OTContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  config: PropTypes.object.isRequired,
  isProvider: PropTypes.bool,
  initialPendingInvitations: PropTypes.array,
};



// WEBPACK FOOTER //
// ./src/redux/opentok/context.jsx